import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = ["tabLink", "pane"]

  connect() {
    const activeTab = this.tabLinkTargets.find(tab => tab.classList.contains("active"))
    if (activeTab) {
      this.fetchTabContent(activeTab)
    }

    this.clickHandler = this.handleActionClick.bind(this)
    this.element.addEventListener("click", this.clickHandler)
  }

  disconnect() {
    this.element.removeEventListener("click", this.clickHandler)
  }

  changeTab(event) {
    event.preventDefault()
    this.fetchTabContent(event.currentTarget)
  }

  // --- TAB LOADING ENGINE ---
  async fetchTabContent(tabElement) {
    const path = tabElement.dataset.url
    const targetPaneId = tabElement.getAttribute("href").replace("#", "")
    const targetPane = this.paneTargets.find(pane => pane.id === targetPaneId)

    if (!path || !targetPane) return
    if (targetPane.dataset.loaded === "true") return

    this.showLoader(targetPane, targetPaneId)

    try {
      const response = await fetch(path, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Accept": "text/html"
        }
      })

      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)

      const htmlContent = await response.text()
      targetPane.innerHTML = htmlContent
      targetPane.dataset.loaded = "true"

    } catch (error) {
      console.error(`[UserConnections] Error loading ${targetPaneId}:`, error)
    }
  }

  // --- INLINE ACTION INTERCEPTOR ---
  handleActionClick(event) {
    const button = event.target.closest(".ajax") || event.target.closest('a[data-remote="true"]')
    if (!button) return

    event.preventDefault()
    event.stopPropagation()

    const confirmationMessage = button.dataset.confirm

    if (confirmationMessage) {
      this.showCustomConfirmModal(confirmationMessage, () => {
        this.executeRemoteAction(button)
      })
    } else {
      this.executeRemoteAction(button)
    }
  }

  // --- CUSTOM BOOTSTRAP MODAL ORCHESTRATION ---
  showCustomConfirmModal(message, onConfirmCallback) {
    // Scopes search strictly to the current controller container on this page
    const modalContainer = this.element.querySelector("#connections-modal-container")
    if (!modalContainer) return

    const modalElement = modalContainer.querySelector("#dynamicConfirmModal")
    const messageElement = modalContainer.querySelector("#dynamicConfirmModalMessage")
    const confirmButton = modalContainer.querySelector("#modal-confirm-execute")
    if (!modalElement || !messageElement || !confirmButton) return

    messageElement.textContent = message

    const bsModalInstance = new window.bootstrap.Modal(modalElement)

    const handleConfirm = () => {
      bsModalInstance.hide()
      onConfirmCallback()
    }

    confirmButton.addEventListener("click", handleConfirm, { once: true })
    bsModalInstance.show()

    modalElement.addEventListener("hidden.bs.modal", () => {
      confirmButton.removeEventListener("click", handleConfirm)
      messageElement.textContent = ""
    }, { once: true })
  }

  // --- AJAX DATA SEND OPERATIONS ---
  async executeRemoteAction(button) {
    const url = button.getAttribute("href")
    const method = (button.dataset.method || "POST").toUpperCase()

    this.freezeButton(button, true)

    try {
      const requestOptions = {
        method: method,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Accept": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      }

      if (method !== "GET") {
        const payloadData = {}
        Object.keys(button.dataset).forEach(key => {
          if (!["method", "confirm", "remote", "url", "originalHtml", "loaded"].includes(key)) {
            payloadData[key] = button.dataset[key]
            const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase()
            if (snakeKey !== key) payloadData[snakeKey] = button.dataset[key]
          }
        })

        if (Object.keys(payloadData).length > 0) {
          requestOptions.headers["Content-Type"] = "application/json"
          requestOptions.body = JSON.stringify(payloadData)
        }
      }

      const response = await fetch(url, requestOptions)
      if (!response.ok) throw new Error(`HTTP Action Error: ${response.status}`)

      const payload = await response.json()

      this.freezeButton(button, false)
      this.processResponsePayload(button, payload)

    } catch (error) {
      console.error(`[UserConnections] Action processing failed:`, error)
      this.freezeButton(button, false)
    }
  }

  processResponsePayload(button, payload) {
    if (!payload || !payload.action) return
    const action = payload.action

    const card = button.closest(".listed-user-profile-card")

    if (action.move && action.move.to && card) {
      this.moveCardToPane(card, action.move.to)
    } else if (action.link) {
      const nextLinkState = action.link
      if (nextLinkState.url) button.setAttribute("href", nextLinkState.url)
      if (nextLinkState.method) button.setAttribute("data-method", nextLinkState.method)
      if (nextLinkState.text) button.innerHTML = nextLinkState.text
      if (nextLinkState.classToggle) this.syncBootstrapClasses(button, nextLinkState.classToggle)
    }
  }

  moveCardToPane(card, targetPaneId) {
    const targetPane = this.paneTargets.find(pane => pane.id === targetPaneId)
    const sourcePane = card.closest(".tab-pane")

    card.style.transition = "all 0.3s ease"
    card.style.opacity = "0"
    card.style.transform = "scale(0.8)"

    setTimeout(() => {
      card.remove()

      if (targetPane) {
        targetPane.dataset.loaded = "false"

        const targetTabLink = this.tabLinkTargets.find(tab =>
          tab.getAttribute("href").replace("#", "") === targetPaneId
        )

        if (targetTabLink) {
          this.fetchTabContent(targetTabLink)
        }
      }

      if (sourcePane) {
        const remaining = sourcePane.querySelectorAll(".listed-user-profile-card")
        if (remaining.length === 0) {
          sourcePane.innerHTML = `
            <div class="emptybox text-center p-5">
              <i class="fa-solid fa-users text-muted" style="font-size: 20px"></i>
              <h4 class="mt-2 text-secondary">No connections found here</h4>
            </div>
          `
        }
      }
    }, 300)
  }

  syncBootstrapClasses(button, classString) {
    button.className = "ajax"
    const classes = classString.split(" ")
    classes.forEach(cls => {
      const cleanClass = cls.trim()
      if (cleanClass) {
        button.classList.add(cleanClass)
      }
    })
  }

  freezeButton(button, shouldFreeze) {
    if (shouldFreeze) {
      button.classList.add("disabled")
      button.style.pointerEvents = "none"
    } else {
      button.classList.remove("disabled")
      button.style.pointerEvents = "auto"
    }
  }

  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  showLoader(pane, name) {
    pane.innerHTML = `
      <div class="text-center p-5">
        <div class="spinner-border text-secondary" role="status"></div>
        <h4 class="mt-3 text-muted">Loading ${name.substring(0, 1).toUpperCase() + name.substring(1)}...</h4>
      </div>
    `
  }
}
